import { useState } from "react";
import { X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useSeo } from "@/hooks/useSeo";
import PageHeader from "@/components/PageHeader";
import WorkCard from "@/components/WorkCard";
import CTASection from "@/components/sections/CTASection";
import { work, workGalleries } from "@/data/content";
import { caseStudySchema } from "@/data/seo";

const WORK_CRUMBS = [{ name: "Work", path: "/work" }];
const WORK_SCHEMA = [
  {
    "@type": "ItemList",
    name: "HashTag Marketing case studies",
    itemListElement: work.map((w, i) => ({ "@type": "ListItem", position: i + 1, item: caseStudySchema(w) })),
  },
];

function ArchiveSection() {
  const [active, setActive] = useState(workGalleries[0].id);
  const [lightbox, setLightbox] = useState(null);
  const group = workGalleries.find((g) => g.id === active);
  return (
    <section className="border-t border-line bg-surface" data-testid="work-archive-section">
      <div className="mx-auto max-w-[1600px] px-5 py-14 sm:py-16 md:py-20 lg:py-24 xl:py-28 sm:px-8 md:px-10 lg:px-12 xl:px-16">
        <p className="mb-4 text-xs font-bold uppercase tracking-[0.22em] text-accent">From the archive</p>
        <h2 className="font-display text-4xl font-extrabold leading-[0.95] text-ink md:text-6xl">
          The work, <span className="font-serif italic font-medium">unfiltered.</span>
        </h2>
        <p className="mt-4 max-w-xl text-lg text-muted">
          Press coverage, design and campaign material produced by the studio over the years.
        </p>

        <div className="mt-10 flex flex-wrap gap-3">
          {workGalleries.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => setActive(g.id)}
              data-testid={`archive-tab-${g.id}`}
              className={`rounded-full border px-5 py-2.5 text-sm font-bold uppercase tracking-[0.12em] transition-colors duration-300 ${
                active === g.id
                  ? "border-ink bg-ink text-bone"
                  : "border-line bg-transparent text-muted hover:border-ink hover:text-ink"
              }`}
            >
              {g.title} <span className="ml-1 opacity-60">{g.images.length}</span>
            </button>
          ))}
        </div>

        <p className="mt-6 text-sm text-muted">{group.blurb}</p>

        <motion.div
          key={group.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
          className="mt-8 columns-2 gap-4 md:columns-3 lg:columns-4"
          data-testid={`archive-grid-${group.id}`}
        >
          {group.images.map((src, i) => (
            <button
              key={src}
              type="button"
              onClick={() => setLightbox(src)}
              data-testid={`archive-image-${group.id}-${i}`}
              className="group mb-4 block w-full break-inside-avoid overflow-hidden bg-line"
            >
              <img
                src={src}
                alt={`${group.title} — ${i + 1}`}
                loading="lazy"
                className="w-full transition-transform duration-700 ease-out group-hover:scale-[1.04]"
              />
            </button>
          ))}
        </motion.div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setLightbox(null)}
            data-testid="archive-lightbox"
            className="fixed inset-0 z-[200] flex items-center justify-center bg-ink/90 p-5 backdrop-blur-sm md:p-12"
          >
            <button
              type="button"
              aria-label="Close"
              data-testid="archive-lightbox-close"
              className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-bone text-ink"
            >
              <X size={20} />
            </button>
            <img src={lightbox} alt="Archive item" className="max-h-full max-w-full object-contain" />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

export default function Work() {
  useSeo({
    title: "Case Studies: Branding, PR & Campaign Work",
    description:
      "Case studies from HashTag Marketing, Gurgaon: brand launches, fashion and luxury campaigns, PR, experiential and digital work for clients across India.",
    path: "/work",
    pageType: "CollectionPage",
    breadcrumbs: WORK_CRUMBS,
    schema: WORK_SCHEMA,
  });
  return (
    <>
      <PageHeader
        eyebrow="Selected Work"
        titleLines={["Case", "studies."]}
        intro="Campaigns and brand work across fashion, beauty, lifestyle, interiors and more. Full narratives and verified results are added as each client approves publication."
      />
      <section className="mx-auto max-w-[1600px] px-5 py-14 sm:py-16 md:py-20 lg:py-24 xl:py-28 sm:px-8 md:px-10 lg:px-12 xl:px-16">
        <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:gap-y-14 md:grid-cols-2">
          {work.map((item, i) => (
            <WorkCard key={item.slug} item={item} index={i} className={i % 3 === 1 ? "md:mt-12 lg:mt-16" : ""} />
          ))}
        </div>
      </section>
      <ArchiveSection />
      <CTASection />
    </>
  );
}
